import { useCallback, useEffect, useRef, useState } from 'react';

// Drag-to-resize for a centered modal shell. Desktop/tablet only (≥ sm).
// `resizing` stays true from the first grab until `settleMs` after the last
// release, so heavy children can unmount during the drag and redraw once.
const KEY = 'dividrip:stock-modal-size';
const MIN_W = 720;
const MIN_H = 480;
const MARGIN = 16;

const readSaved = () => {
  try {
    const s = JSON.parse(localStorage.getItem(KEY));
    return s && s.w > 0 && s.h > 0 ? s : null;
  } catch {
    return null;
  }
};

const clamp = (w, h) => ({
  w: Math.round(Math.min(Math.max(w, MIN_W), window.innerWidth - MARGIN * 2)),
  h: Math.round(Math.min(Math.max(h, MIN_H), window.innerHeight - MARGIN * 2)),
});

export default function useModalResize({ settleMs = 500, onStart } = {}) {
  const [enabled, setEnabled] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 640px)').matches);
  const [size, setSize] = useState(readSaved);
  const [resizing, setResizing] = useState(false);
  const timer = useRef(null);
  const drag = useRef(null);
  const sizeRef = useRef(size);
  sizeRef.current = size;

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const onChange = (e) => setEnabled(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const start = useCallback((edge) => (e) => {
    if (!enabled || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const r = e.currentTarget.parentElement.getBoundingClientRect();
    drag.current = { edge, x: e.clientX, y: e.clientY, w: r.width, h: r.height };
    if (timer.current) clearTimeout(timer.current);
    if (!resizing) onStart?.();
    setResizing(true);
    const onMove = (ev) => {
      const d = drag.current;
      if (!d) return;
      const dx = ev.clientX - d.x;
      const dy = ev.clientY - d.y;
      let w = d.w;
      let h = d.h;
      // Shell is flex-centered, so an edge moves half as far as the size changes.
      if (d.edge.includes('e')) w = d.w + dx * 2;
      if (d.edge.includes('w')) w = d.w - dx * 2;
      if (d.edge.includes('s')) h = d.h + dy * 2;
      if (d.edge.includes('n')) h = d.h - dy * 2;
      setSize(clamp(w, h));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      drag.current = null;
      try { if (sizeRef.current) localStorage.setItem(KEY, JSON.stringify(sizeRef.current)); } catch { /* ignore */ }
      timer.current = setTimeout(() => setResizing(false), settleMs);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [enabled, resizing, settleMs, onStart]);

  const reset = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    drag.current = null;
    sizeRef.current = null;
    setSize(null);
    setResizing(false);
    try { localStorage.removeItem(KEY); } catch { /* ignore */ }
  }, []);

  let style;
  if (enabled && size) {
    const c = clamp(size.w, size.h);
    style = { width: c.w, height: c.h, maxWidth: 'none' };
  }
  return { enabled, size, resizing, style, start, reset };
}
